import * as d3 from "d3";
import * as d3_voronoi from "d3-voronoi";
import PriorityQueue from "js-priority-queue";
import { makeName, resetNamePools } from "lib/strings/terrain";
import { buildRoad } from "lib/util/path";
import {
  distance,
  isedge,
  isnearedge,
  neighbours,
  quantile,
} from "lib/util/mesh";
import { random, setSeed } from "lib/util/rng";
import { Map as MapConfig } from "config";
import Debug from "debug";
const debug = Debug("map");

function runif(lo, hi) {
  return lo + random() * (hi - lo);
}

// Stage timings land on the render object rather than in console.time, so the app can ignore them
// and scripts/mapstat.js can read them. Generation is ~1.5-2.7s of blank screen at mount and the
// stages that were instrumented were the cheap half of it — see scripts/mapstat.js.
function time(render, label, fn) {
  const start = Date.now();
  const value = fn();
  render.timings = render.timings || {};
  render.timings[label] = Date.now() - start;
  return value;
}

// Box-Muller produces two normals per pass and banks the second. That bank is module state, so a
// second GameMap() in the same process would open with a number drawn from the previous world —
// which is invisible in the app (one map per page load) and fatal to the harness, which runs many.
// resetRnorm() is called at the top of GameMap.
var z2 = null;

function resetRnorm() {
  z2 = null;
}

function rnorm() {
  if (z2 != null) {
    var tmp = z2;
    z2 = null;
    return tmp;
  }
  var x1 = 0;
  var x2 = 0;
  var w = 2.0;
  while (w >= 1) {
    x1 = runif(-1, 1);
    x2 = runif(-1, 1);
    w = x1 * x1 + x2 * x2;
  }
  w = Math.sqrt((-2 * Math.log(w)) / w);
  z2 = x2 * w;
  return x1 * w;
}

function randomVector(scale) {
  return [scale * rnorm(), scale * rnorm()];
}

var defaultExtent = {
  width: 1,
  height: 1,
};

// Only for a caller that hands in a params object without one — config/index.js is where these
// live and where they are explained.
const DEFAULT_TERRAIN = {
  tilt: 2,
  cone: [-1.5, -1.1],
  mountains: 70,
  sea: [0.08, 0.16],
};

function generateGoodPoints(n, extent = defaultExtent) {
  // Directly generate points and sort them in one step to reduce overhead
  let pts = Array.from({ length: n }, () => [
    (random() - 0.5) * extent.width,
    (random() - 0.5) * extent.height,
  ]).sort((a, b) => a[0] - b[0]);

  // Apply improvement directly without reassignment
  return improvePoints(pts, 1, extent);
}

function centroid(pts) {
  var x = 0;
  var y = 0;
  for (var i = 0; i < pts.length; i++) {
    x += pts[i][0];
    y += pts[i][1];
  }
  return [x / pts.length, y / pts.length];
}

function improvePoints(pts, n, extent) {
  n = n || 1;
  extent = extent || defaultExtent;
  for (var i = 0; i < n; i++) {
    pts = voronoi(pts, extent).polygons(pts).map(centroid);
  }
  return pts;
}

function voronoi(pts, extent) {
  extent = extent || defaultExtent;
  var w = extent.width / 2;
  var h = extent.height / 2;
  return d3_voronoi.voronoi().extent([
    [-w, -h],
    [w, h],
  ])(pts);
}

function makeMesh(pts, extent) {
  extent = extent || defaultExtent;
  var vor = voronoi(pts, extent);
  var vxs = []; // Coordinates
  var vxids = {}; // ??
  var adj = []; // ?? Adjacent
  var edges = []; // ?? Edges
  var tris = []; // ?? Tris
  for (let i = 0; i < vor.edges.length; i++) {
    let e = vor.edges[i];
    if (e === undefined) continue;
    let e0 = vxids[e[0]];
    let e1 = vxids[e[1]];
    if (e0 === undefined) {
      e0 = vxs.length;
      vxids[e[0]] = e0;
      vxs.push(e[0]);
    }
    if (e1 === undefined) {
      e1 = vxs.length;
      vxids[e[1]] = e1;
      vxs.push(e[1]);
    }
    adj[e0] = adj[e0] || [];
    adj[e0].push(e1);
    adj[e1] = adj[e1] || [];
    adj[e1].push(e0);
    edges.push([e0, e1, e.left, e.right]);
    tris[e0] = tris[e0] || [];
    if (!tris[e0].includes(e.left)) tris[e0].push(e.left);
    if (e.right && !tris[e0].includes(e.right)) tris[e0].push(e.right);
    tris[e1] = tris[e1] || [];
    if (!tris[e1].includes(e.left)) tris[e1].push(e.left);
    if (e.right && !tris[e1].includes(e.right)) tris[e1].push(e.right);
  }

  var mesh = {
    pts: pts,
    vor: vor,
    vxs: vxs,
    adj: adj,
    tris: tris,
    edges: edges,
    extent: extent,
  };
  mesh.map = function (f) {
    var mapped = vxs.map(f);
    mapped.mesh = mesh;
    return mapped;
  };
  return mesh;
}

function generateGoodMesh(n, extent) {
  extent = extent || defaultExtent;
  var pts = generateGoodPoints(n, extent);
  return makeMesh(pts, extent);
}


// Retrieves the indices of all neighboring vertices of a given vertex in the mesh.
// mesh: The data structure representing the Voronoi diagram or graph.
// i: The index of the vertex for which to find neighbors.



function zero(mesh) {
  var z = [];
  for (var i = 0; i < mesh.vxs.length; i++) {
    z[i] = 0;
  }
  z.mesh = mesh;
  return z;
}

function slope(mesh, direction) {
  return mesh.map(function (x) {
    return x[0] * direction[0] + x[1] * direction[1];
  });
}

function cone(mesh, slope) {
  return mesh.map(function (x) {
    return Math.pow(x[0] * x[0] + x[1] * x[1], 0.5) * slope;
  });
}

function map(h, f) {
  var newh = h.map(f);
  newh.mesh = h.mesh;
  return newh;
}

function normalize(h) {
  var lo = d3.min(h);
  var hi = d3.max(h);
  return map(h, function (x) {
    return (x - lo) / (hi - lo);
  });
}

function peaky(h) {
  return map(normalize(h), Math.sqrt);
}

function add() {
  var n = arguments[0].length;
  var newvals = zero(arguments[0].mesh);
  for (var i = 0; i < n; i++) {
    for (var j = 0; j < arguments.length; j++) {
      newvals[i] += arguments[j][i];
    }
  }
  return newvals;
}

function mountains(mesh, n, r) {
  r = r || 0.05;
  var mounts = [];
  for (var i = 0; i < n; i++) {
    mounts.push([
      mesh.extent.width * (random() - 0.5),
      mesh.extent.height * (random() - 0.5),
    ]);
  }
  var newvals = zero(mesh);
  for (var i = 0; i < mesh.vxs.length; i++) {
    var p = mesh.vxs[i];
    for (var j = 0; j < n; j++) {
      var m = mounts[j];
      newvals[i] += Math.pow(
        Math.exp(
          -((p[0] - m[0]) * (p[0] - m[0]) + (p[1] - m[1]) * (p[1] - m[1])) /
            (2 * r * r),
        ),
        2,
      );
    }
  }
  return newvals;
}

// I use Lloyd relaxation to improve the point set.
function relax(h) {
  var newh = zero(h.mesh);
  for (var i = 0; i < h.length; i++) {
    var nbs = neighbours(h.mesh, i);
    if (nbs.length < 3) {
      newh[i] = 0;
      continue;
    }
    newh[i] = d3.mean(
      nbs.map(function (j) {
        return h[j];
      }),
    );
  }
  return newh;
}

function downhill(h) {
  if (h.downhill) return h.downhill;
  function downfrom(i) {
    if (isedge(h.mesh, i)) return -2;
    var best = -1;
    var besth = h[i];
    var nbs = neighbours(h.mesh, i);
    for (var j = 0; j < nbs.length; j++) {
      if (h[nbs[j]] < besth) {
        besth = h[nbs[j]];
        best = nbs[j];
      }
    }
    return best;
  }
  var downs = [];
  for (var i = 0; i < h.length; i++) {
    downs[i] = downfrom(i);
  }
  h.downhill = downs;
  return downs;
}

/**
 * Fill every depression up to its lowest outlet, so water always runs downhill to the edge.
 *
 * Priority flood: start from the margin, always expand from the lowest resolved vertex, and give
 * each vertex it reaches either its own height or a hair above the one it was reached from —
 * whichever is greater. Because vertices are settled in ascending height order, the first way in to
 * a vertex is the lowest way in, so one pass is enough and nothing needs revisiting.
 *
 * This replaced a relaxation that swept the whole mesh over and over until a pass changed nothing.
 * That version was correct and it was the single most expensive thing in world generation:
 * `doErosion(h, amount, 5)` calls it six times, and at ~190ms a call that was most of the ~1.5s
 * erosion stage on its own.
 */
function fillSinks(h, epsilon) {
  epsilon = epsilon || 1e-5;

  var newh = zero(h.mesh);
  var resolved = new Uint8Array(h.length);
  var queue = new PriorityQueue({
    comparator: function (a, b) {
      return a.height - b.height;
    },
  });

  // The margin drains freely: it keeps its own height and is where the flood starts.
  for (var i = 0; i < h.length; i++) {
    if (!isnearedge(h.mesh, i)) continue;
    newh[i] = h[i];
    resolved[i] = 1;
    queue.queue({ index: i, height: h[i] });
  }

  while (queue.length > 0) {
    var index = queue.dequeue().index;
    var nbs = neighbours(h.mesh, index);
    for (var j = 0; j < nbs.length; j++) {
      var nb = nbs[j];
      if (resolved[nb]) continue;
      newh[nb] = Math.max(h[nb], newh[index] + epsilon);
      resolved[nb] = 1;
      queue.queue({ index: nb, height: newh[nb] });
    }
  }

  // Anything the flood never reached is cut off from the margin entirely. Leave it as it was.
  for (var k = 0; k < h.length; k++) {
    if (!resolved[k]) newh[k] = h[k];
  }

  return newh;
}

// Memoised on the height field, exactly as `downhill` is above — and for the same reason. Erosion
// hands in a new `h` each round so it recomputes as it should, but placeCities calls cityScore once
// per city against one unchanging field, and each of those was a fresh O(n log n) sort of every
// vertex in the world. Fifteen cities meant fourteen of them were wasted.
function getFlux(h) {
  if (h.flux) return h.flux;
  var dh = downhill(h);
  var idxs = [];
  var flux = zero(h.mesh);
  for (var i = 0; i < h.length; i++) {
    idxs[i] = i;
    flux[i] = 1 / h.length;
  }
  idxs.sort(function (a, b) {
    return h[b] - h[a];
  });
  for (var i = 0; i < h.length; i++) {
    var j = idxs[i];
    if (dh[j] >= 0) {
      flux[dh[j]] += flux[j];
    }
  }
  // Every caller treats this as read-only — cityScore copies it through `map` before scoring — so
  // one array can be shared.
  h.flux = flux;
  return flux;
}

function getSlope(h) {
  var slope = zero(h.mesh);
  for (var i = 0; i < h.length; i++) {
    var s = trislope(h, i);
    slope[i] = Math.sqrt(s[0] * s[0] + s[1] * s[1]);
  }
  return slope;
}

function erosionRate(h) {
  var flux = getFlux(h);
  var slope = getSlope(h);
  var newh = zero(h.mesh);
  for (var i = 0; i < h.length; i++) {
    var river = Math.sqrt(flux[i]) * slope[i];
    var creep = slope[i] * slope[i];
    var total = 1000 * river + creep;
    total = total > 200 ? 200 : total;
    newh[i] = total;
  }
  return newh;
}

function erode(h, amount) {
  var er = erosionRate(h);
  var newh = zero(h.mesh);
  var maxr = d3.max(er);
  for (var i = 0; i < h.length; i++) {
    newh[i] = h[i] - amount * (er[i] / maxr);
  }
  return newh;
}

function doErosion(h, amount, n) {
  n = n || 1;
  h = fillSinks(h);
  for (var i = 0; i < n; i++) {
    h = erode(h, amount);
    h = fillSinks(h);
  }
  return h;
}

function setSeaLevel(h, q) {
  var newh = zero(h.mesh);
  var delta = quantile(h, q);
  for (var i = 0; i < h.length; i++) {
    newh[i] = h[i] - delta;
  }
  return newh;
}

function cleanCoast(h, iters) {
  for (var iter = 0; iter < iters; iter++) {
    var changed = 0;
    var newh = zero(h.mesh);
    for (var i = 0; i < h.length; i++) {
      newh[i] = h[i];
      var nbs = neighbours(h.mesh, i);
      if (h[i] <= 0 || nbs.length != 3) continue;
      var count = 0;
      var best = -999999;
      for (var j = 0; j < nbs.length; j++) {
        if (h[nbs[j]] > 0) {
          count++;
        } else if (h[nbs[j]] > best) {
          best = h[nbs[j]];
        }
      }
      if (count > 1) continue;
      newh[i] = best / 2;
      changed++;
    }
    h = newh;
    newh = zero(h.mesh);
    for (var i = 0; i < h.length; i++) {
      newh[i] = h[i];
      var nbs = neighbours(h.mesh, i);
      if (h[i] > 0 || nbs.length != 3) continue;
      var count = 0;
      var best = 999999;
      for (var j = 0; j < nbs.length; j++) {
        if (h[nbs[j]] <= 0) {
          count++;
        } else if (h[nbs[j]] < best) {
          best = h[nbs[j]];
        }
      }
      if (count > 1) continue;
      newh[i] = best / 2;
      changed++;
    }
    h = newh;
  }
  return h;
}

function trislope(h, i) {
  var nbs = neighbours(h.mesh, i);
  if (nbs.length != 3) return [0, 0];
  var p0 = h.mesh.vxs[nbs[0]];
  var p1 = h.mesh.vxs[nbs[1]];
  var p2 = h.mesh.vxs[nbs[2]];

  var x1 = p1[0] - p0[0];
  var x2 = p2[0] - p0[0];
  var y1 = p1[1] - p0[1];
  var y2 = p2[1] - p0[1];

  var det = x1 * y2 - x2 * y1;
  var h1 = h[nbs[1]] - h[nbs[0]];
  var h2 = h[nbs[2]] - h[nbs[0]];

  return [(y2 * h1 - y1 * h2) / det, (-x2 * h1 + x1 * h2) / det];
}

function cityScore(h, cities, render) {
  let score = map(getFlux(h), Math.sqrt);
  let bestIndex = 0;
  let maxScore = -999999;
  if (render.possibleCities.length === 0) {
    for (let i = 0; i < h.length; i++) {
      if (h[i] <= 0 || isnearedge(h.mesh, i)) {
        score[i] = -999999;
        continue;
      } else {
        render.possibleCities.push(i);
      }
    }
  }

  for (let k = 0; k < render.possibleCities.length; k++) {
    const i = render.possibleCities[k];

    score[i] += 0.01 /
      (1e-9 + Math.abs(h.mesh.vxs[i][0]) - h.mesh.extent.width / 2);
    score[i] += 0.01 /
      (1e-9 + Math.abs(h.mesh.vxs[i][1]) - h.mesh.extent.height / 2);
    for (let j = 0; j < cities.length; j++) {
      score[i] -= 0.02 / (distance(h.mesh, cities[j], i) + 1e-9);
    }
    if (score[i] > maxScore) {
      maxScore = score[i];
      bestIndex = i;
    }
  }
  return bestIndex;
}

function placeCity(render) {
  render.cities = render.cities || [];
  const newCityIndex = cityScore(render.h, render.cities, render);
  render.cities.push(newCityIndex);
}

function placeCities(render) {
  var params = render.params;
  var h = render.h;
  var n = params.ncities;
  render.possibleCities = [];
  for (var i = 0; i < n; i++) {
    placeCity(render);
  }

  render.cities.sort((a, b) => {
    return h.mesh.vxs[b][0] - h.mesh.vxs[a][0];
  });

  // A scratch list memoising the candidate scan across the ncities cityScore calls. Done with it —
  // and it is 30k indices that would otherwise sit in gameState for the life of the run.
  delete render.possibleCities;
}

function contour(h, level) {
  level = level || 0;
  var edges = [];
  for (var i = 0; i < h.mesh.edges.length; i++) {
    var e = h.mesh.edges[i];
    if (e[3] == undefined) continue;
    if (isnearedge(h.mesh, e[0]) || isnearedge(h.mesh, e[1])) continue;
    if (
      (h[e[0]] > level && h[e[1]] <= level) ||
      (h[e[1]] > level && h[e[0]] <= level)
    ) {
      edges.push([e[2], e[3]]);
    }
  }
  return mergeSegments(edges);
}

function getRivers(h, limit) {
  const metadata = { indices: [], neighbours: [] };

  var dh = downhill(h);
  var flux = getFlux(h);
  var links = [];
  var above = 0;
  for (var i = 0; i < h.length; i++) {
    if (h[i] > 0) above++;
  }
  limit *= above / h.length;
  for (var i = 0; i < dh.length; i++) {
    if (isnearedge(h.mesh, i)) continue;
    if (flux[i] > limit && h[i] > 0 && dh[i] >= 0) {
      var up = h.mesh.vxs[i];
      var down = h.mesh.vxs[dh[i]];
      metadata.indices.push(i);
      metadata.neighbours = metadata.neighbours.concat(neighbours(h.mesh, i));
      if (h[dh[i]] > 0) {
        links.push([up, down]);
      } else {
        links.push([up, [(up[0] + down[0]) / 2, (up[1] + down[1]) / 2]]);
      }
    }
  }
  return {
    paths: mergeSegments(links).map(relaxPath),
    metadata,
  };
}

function getTerritories(render) {
  var h = render.h;
  var cities = render.cities;
  var n = render.params.nterrs;
  if (n > render.cities.length) n = render.cities.length;
  var flux = getFlux(h);
  var terr = [];
  var queue = new PriorityQueue({
    comparator: function (a, b) {
      return a.score - b.score;
    },
  });
  function weight(u, v) {
    var horiz = distance(h.mesh, u, v);
    var vert = h[v] - h[u];
    if (vert > 0) vert /= 10;
    var diff = 1 + 0.25 * Math.pow(vert / horiz, 2);
    diff += 100 * Math.sqrt(flux[u]);
    if (h[u] <= 0) diff = 100;
    if (h[u] > 0 != h[v] > 0) return 1000;
    return horiz * diff;
  }
  for (var i = 0; i < n; i++) {
    terr[cities[i]] = cities[i];
    var nbs = neighbours(h.mesh, cities[i]);
    for (var j = 0; j < nbs.length; j++) {
      queue.queue({
        score: weight(cities[i], nbs[j]),
        city: cities[i],
        vx: nbs[j],
      });
    }
  }
  while (queue.length) {
    var u = queue.dequeue();
    if (terr[u.vx] !== undefined) continue;
    terr[u.vx] = u.city;
    var nbs = neighbours(h.mesh, u.vx);
    for (var i = 0; i < nbs.length; i++) {
      var v = nbs[i];
      if (terr[v] != undefined) continue;
      var newdist = weight(u.vx, v);
      queue.queue({
        score: u.score + newdist,
        city: u.city,
        vx: v,
      });
    }
  }
  terr.mesh = h.mesh;
  return terr;
}

function getBorders(render) {
  var terr = render.terr;
  var h = render.h;
  var edges = [];
  for (var i = 0; i < terr.mesh.edges.length; i++) {
    var e = terr.mesh.edges[i];
    if (e[3] === undefined) continue;
    if (isnearedge(terr.mesh, e[0]) || isnearedge(terr.mesh, e[1])) continue;
    if (h[e[0]] < 0 || h[e[1]] < 0) continue;
    if (terr[e[0]] !== terr[e[1]]) {
      edges.push([e[2], e[3]]);
    }
  }
  return mergeSegments(edges).map(relaxPath);
}

function mergeSegments(segs) {
  var adj = {};
  for (var i = 0; i < segs.length; i++) {
    var seg = segs[i];
    var a0 = adj[seg[0]] || [];
    var a1 = adj[seg[1]] || [];
    a0.push(seg[1]);
    a1.push(seg[0]);
    adj[seg[0]] = a0;
    adj[seg[1]] = a1;
  }
  var done = [];
  var paths = [];
  var path = null;
  while (true) {
    if (path == null) {
      for (var i = 0; i < segs.length; i++) {
        if (done[i]) continue;
        done[i] = true;
        path = [segs[i][0], segs[i][1]];
        break;
      }
      if (path == null) break;
    }
    var changed = false;
    for (var i = 0; i < segs.length; i++) {
      if (done[i]) continue;
      if (adj[path[0]].length == 2 && segs[i][0] == path[0]) {
        path.unshift(segs[i][1]);
      } else if (adj[path[0]].length == 2 && segs[i][1] == path[0]) {
        path.unshift(segs[i][0]);
      } else if (
        adj[path[path.length - 1]].length == 2 &&
        segs[i][0] == path[path.length - 1]
      ) {
        path.push(segs[i][1]);
      } else if (
        adj[path[path.length - 1]].length == 2 &&
        segs[i][1] == path[path.length - 1]
      ) {
        path.push(segs[i][0]);
      } else {
        continue;
      }
      done[i] = true;
      changed = true;
      break;
    }
    if (!changed) {
      paths.push(path);
      path = null;
    }
  }
  return paths;
}

function relaxPath(path) {
  var newpath = [path[0]];
  for (var i = 1; i < path.length - 1; i++) {
    var newpt = [
      0.25 * path[i - 1][0] + 0.5 * path[i][0] + 0.25 * path[i + 1][0],
      0.25 * path[i - 1][1] + 0.5 * path[i][1] + 0.25 * path[i + 1][1],
    ];
    newpath.push(newpt);
  }
  newpath.push(path[path.length - 1]);
  return newpath;
}

/**
 * The slope magnitude at every vertex. Gameplay input: the router prices a step by it and the
 * terrain classifier names the ground by it.
 *
 * Split out of getSlopes, which computed this and the SVG hatching in one pass and returned them as
 * a pair. The hatching half draws random jitter (runif, rnorm) partway through that loop, so under
 * a pinned seed the *gameplay* slope values downstream would have depended on how the map was going
 * to be drawn. Two separate passes cost a second walk over the mesh and make the seed mean
 * something stable.
 */
function computeSlopeData(heightMap) {
  // `magnitude` is the gameplay number and stays a plain Map of index -> number, which is what the
  // router and the classifier read. `components` is the same pass's working values, kept only so
  // the hatching does not have to walk the mesh a second time to redraw them.
  const magnitude = new Map();
  const components = new Map();

  for (var i = 0; i < heightMap.length; i++) {
    // Below sea level or out at the edge is not land, and carries no slope.
    if (heightMap[i] <= 0 || isnearedge(heightMap.mesh, i)) {
      magnitude.set(i, 0);
      continue;
    }

    var nbs = neighbours(heightMap.mesh, i);
    nbs.push(i); // include self, for a slope relative to this vertex

    var horizontal = 0;
    var vertical = 0;
    for (var j = 0; j < nbs.length; j++) {
      var parts = trislope(heightMap, nbs[j]);
      horizontal += parts[0] / 10;
      vertical += parts[1];
    }
    horizontal /= nbs.length;
    vertical /= nbs.length;

    magnitude.set(i, Math.sqrt(horizontal * horizontal + vertical * vertical));
    components.set(i, [horizontal, vertical]);
  }

  return { magnitude, components };
}

/**
 * The hatching that stands for relief on the map. Purely for the SVG — nothing reads it but
 * visualizeSlopes in pages/Map.js — and deliberately jittered, so it is drawn like a hand would.
 */
function slopeStrokes(heightMap, components) {
  var strokes = [];
  var radius = 0.25 / Math.sqrt(heightMap.length);

  for (var i = 0; i < heightMap.length; i++) {
    var parts = components.get(i);
    if (!parts) continue;

    var horizontal = parts[0];
    var vertical = parts[1];

    // Too gentle to be worth a mark.
    if (Math.abs(horizontal) < runif(0.1, 0.4)) continue;

    var lineLength = radius *
      runif(1, 2) *
      (1 - 0.2 * Math.pow(Math.atan(horizontal), 2)) *
      Math.exp(vertical / 100);
    var xPos = heightMap.mesh.vxs[i][0];
    var yPos = heightMap.mesh.vxs[i][1];

    // Steep ground is drawn as several short marks rather than one long one.
    if (Math.abs(lineLength * horizontal) > 2 * radius) {
      var segmentCount = Math.floor(Math.abs((lineLength * horizontal) / radius));
      lineLength /= segmentCount;
      if (segmentCount > 4) segmentCount = 4; // any more is clutter

      for (var k = 0; k < segmentCount; k++) {
        var dx = rnorm() * radius;
        var dy = rnorm() * radius;
        strokes.push([
          [xPos + dx - lineLength, yPos + dy + lineLength * horizontal],
          [xPos + dx + lineLength, yPos + dy - lineLength * horizontal],
        ]);
      }
    } else {
      strokes.push([
        [xPos - lineLength, yPos + lineLength * horizontal],
        [xPos + lineLength, yPos - lineLength * horizontal],
      ]);
    }
  }

  return strokes;
}

function generateCoast(params, render) {
  // Sub-stage timings, namespaced under the stage that contains them so the harness can
  // report them without double-counting. See scripts/mapstat.js.
  const step = (label, fn) => (render ? time(render, label, fn) : fn());

  // The three primitives and the sea level cut are config, not constants — they are the shape of
  // the country, and the values they used to hold made a world 40% ocean. See config/index.js for
  // what each does and why it is where it is. Defaulted here so a caller passing a bare params
  // object still generates a world.
  const terrain = params.terrain || DEFAULT_TERRAIN;

  // I start by selecting points at random within the map.
  var mesh = step("coast.mesh", () => generateGoodMesh(params.npts, params.extent));

  //  I start with a simple 'proto-landscape', built with geometric primitives.
  //  This lets me control the broad outlines of the terrain,
  //  while leaving the details for the more physical processes to fill in later.
  var h = step("coast.proto", () => add(
    slope(mesh, randomVector(terrain.tilt)), // Constant slope - if you want to pretend this is physically motivated, think of it as tectonic uplift on one side of the map
    cone(mesh, runif(terrain.cone[0], terrain.cone[1])), // Cone shapes - these can be islands or mountains, or if inverted, lakes or seas
    mountains(mesh, terrain.mountains), // Rounded blobs - these make better hills, and can be scattered all around to make a noisy surface
  ));

  // I use Lloyd relaxation to improve the point set.
  h = step("coast.relax", () => {
    let out = h;
    for (var i = 0; i < 10; i++) {
      out = relax(out); // replace each height value with the average of its neighbours, to smooth the surface
    }
    return peaky(out); // Normalize - rescale the heights to lie in the range 0-1
  });

  /**  start by tracing the routes that water would take over the grid.
   * For each grid point, we say that water flows to its lowest neighbour,
   * and so on down until we reach the edge of the map.
   * This gives a map of water flow. **/
  h = step("coast.erosion", () => doErosion(h, runif(0, 0.1), 5));

  // erosion always lowers the surface, so it usually helps to drop the sea level afterwards to match.
  // when we reach gridpoints which are lower than all of their neighbours.
  // Do we route the water back uphill?
  // This will probably lead to cycles in the water system, which are trouble.
  // Instead, we want to fill in these gaps (often called sinks or depressions),
  // so that the water always runs downhill all the way to the edge.
  h = step("coast.fillSinks", () => fillSinks(setSeaLevel(h, runif(terrain.sea[0], terrain.sea[1]))));

  // The erosion tends to produce quite rough terrain, which becomes tiny islands when cut off by sea level.
  // A few of these can look good, but too many just looks messy.
  // I repeatedly apply a filter where points which are below sea level,
  // but a majority of whose neighbours are above sea level, get pulled up,
  // and vice versa for points which are above sea level and have undersea neighbours.
  // A couple of repeats of this produces a much cleaner coastline.
  h = step("coast.cleanCoast", () => cleanCoast(h, 3));
  return h;
}

function terrCenter(h, terr, city, landOnly) {
  var x = 0;
  var y = 0;
  var n = 0;
  for (var i = 0; i < terr.length; i++) {
    if (terr[i] != city) continue;
    if (landOnly && h[i] <= 0) continue;
    x += terr.mesh.vxs[i][0];
    y += terr.mesh.vxs[i][1];
    n++;
  }
  return [x / n, y / n];
}

const penalty = (label, h, citylabels, cities, avoids) => {
  let penalty = 0;
  if (label.x0 < -0.45 * h.mesh.extent.width) penalty += 100;
  if (label.x1 > 0.45 * h.mesh.extent.width) penalty += 100;
  if (label.y0 < -0.45 * h.mesh.extent.height) penalty += 100;
  if (label.y1 > 0.45 * h.mesh.extent.height) penalty += 100;
  for (let i = 0; i < citylabels.length; i++) {
    let olabel = citylabels[i];
    if (
      label.x0 < olabel.x1 && label.x1 > olabel.x0 && label.y0 < olabel.y1 &&
      label.y1 > olabel.y0
    ) {
      penalty += 100;
    }
  }

  for (let i = 0; i < cities.length; i++) {
    let c = h.mesh.vxs[cities[i]];
    if (
      label.x0 < c[0] && label.x1 > c[0] && label.y0 < c[1] && label.y1 > c[1]
    ) {
      penalty += 100;
    }
  }
  for (let i = 0; i < avoids.length; i++) {
    let avoid = avoids[i];
    for (let j = 0; j < avoid.length; j++) {
      let avpath = avoid[j];
      for (let k = 0; k < avpath.length; k++) {
        let pt = avpath[k];
        if (
          pt[0] > label.x0 && pt[0] < label.x1 && pt[1] > label.y0 &&
          pt[1] < label.y1
        ) {
          penalty++;
        }
      }
    }
  }
  return penalty;
};

const getCityLabels = (cities, h, terr, nterrs, params, avoids) => {
  let citylabels = [];
  for (let i = 0; i < cities.length; i++) {
    let x = h.mesh.vxs[cities[i]][0];
    let y = h.mesh.vxs[cities[i]][1];
    let text = makeName("city");
    let size = i < nterrs ? params.fontsizes.city : params.fontsizes.town;
    let sx = ((0.65 * size) / 1000) * text.length;
    let sy = size / 1000;
    let posslabels = [
      {
        x: x + 0.8 * sy,
        y: y + 0.3 * sy,
        align: "start",
        x0: x + 0.7 * sy,
        y0: y - 0.6 * sy,
        x1: x + 0.7 * sy + sx,
        y1: y + 0.6 * sy,
      },
      {
        x: x - 0.8 * sy,
        y: y + 0.3 * sy,
        align: "end",
        x0: x - 0.9 * sy - sx,
        y0: y - 0.7 * sy,
        x1: x - 0.9 * sy,
        y1: y + 0.7 * sy,
      },
      {
        x: x,
        y: y - 0.8 * sy,
        align: "middle",
        x0: x - sx / 2,
        y0: y - 1.9 * sy,
        x1: x + sx / 2,
        y1: y - 0.7 * sy,
      },
      {
        x: x,
        y: y + 1.2 * sy,
        align: "middle",
        x0: x - sx / 2,
        y0: y + 0.1 * sy,
        x1: x + sx / 2,
        y1: y + 1.3 * sy,
      },
    ];
    let label = posslabels[
      d3.scan(posslabels, function (a, b) {
        return penalty(a, h, citylabels, cities, avoids) -
          penalty(b, h, citylabels, cities, avoids);
      })
    ];
    label.text = text;
    label.size = size;
    label.id = cities[i];
    label.territory = terr[cities[i]];
    citylabels.push(label);
  }

  return citylabels;
};

const getRegionLabels = (cities, h, terr, nterrs, params, citylabels) => {
  let reglabels = [];
  for (let i = 0; i < nterrs; i++) {
    let city = cities[i];
    let text = makeName("region");

    let sy = params.fontsizes.region / 1000;
    let sx = 0.6 * text.length * sy;
    let lc = terrCenter(h, terr, city, true);
    let oc = terrCenter(h, terr, city, false);
    let best = 0;
    let bestscore = -999999;
    for (let j = 0; j < h.length; j++) {
      let score = 0;
      let v = h.mesh.vxs[j];
      score -= 3000 *
        Math.sqrt(
          (v[0] - lc[0]) * (v[0] - lc[0]) + (v[1] - lc[1]) * (v[1] - lc[1]),
        );
      score -= 1000 *
        Math.sqrt(
          (v[0] - oc[0]) * (v[0] - oc[0]) + (v[1] - oc[1]) * (v[1] - oc[1]),
        );
      if (terr[j] !== city) score -= 3000;
      for (let k = 0; k < cities.length; k++) {
        let u = h.mesh.vxs[cities[k]];
        if (Math.abs(v[0] - u[0]) < sx && Math.abs(v[1] - sy / 2 - u[1]) < sy) {
          score -= k < nterrs ? 4000 : 500;
        }
        if (
          v[0] - sx / 2 < citylabels[k].x1 &&
          v[0] + sx / 2 > citylabels[k].x0 &&
          v[1] - sy < citylabels[k].y1 &&
          v[1] > citylabels[k].y0
        ) {
          score -= 5000;
        }
      }
      for (let k = 0; k < reglabels.length; k++) {
        let label = reglabels[k];
        if (
          v[0] - sx / 2 < label.x + label.width / 2 &&
          v[0] + sx / 2 > label.x - label.width / 2 &&
          v[1] - sy < label.y &&
          v[1] > label.y - label.size
        ) {
          score -= 20000;
        }
      }
      if (h[j] <= 0) score -= 500;
      if (v[0] + sx / 2 > 0.5 * h.mesh.extent.width) score -= 50000;
      if (v[0] - sx / 2 < -0.5 * h.mesh.extent.width) score -= 50000;
      if (v[1] > 0.5 * h.mesh.extent.height) score -= 50000;
      if (v[1] - sy < -0.5 * h.mesh.extent.height) score -= 50000;
      if (score > bestscore) {
        bestscore = score;
        best = j;
      }
    }
    reglabels.push({
      text: text,
      x: h.mesh.vxs[best][0],
      y: h.mesh.vxs[best][1],
      size: sy,
      width: sx,
      id: city,
    });
  }

  return reglabels;
};

const getLabels = (render) => {
  let { params, h, terr, cities, rivers, coasts, borders } = render;
  let nterrs = params.nterrs;
  let avoids = [rivers.paths, coasts, borders];

  let citylabels = getCityLabels(cities, h, terr, nterrs, params, avoids);
  let reglabels = getRegionLabels(cities, h, terr, nterrs, params, citylabels);

  return [citylabels, reglabels];
};

function getPolitical(cities, regions, roads) {
  let political = new Map();
  cities.forEach((entry) =>
    political.set(entry.id, {
      cityName: entry.text,
      // Optional: a town whose vertex was never claimed by the territory flood has no region, and
      // an unguarded .text here threw inside GameContext's mount effect — before anything rendered.
      // City's constructor already tolerates a missing one (isCapital = !!terrName).
      territory: regions.find((reg) => reg.id === entry.territory)?.text ?? null,
      step: roads.indexOf(entry.id),
      position: entry.id,
    })
  );
  regions.forEach((entry) =>
    political.set(entry.id, {
      ...political.get(entry.id),
      terrName: entry.text,
    })
  );

  return political;
}

/**
 * The cut-points the router and the terrain classifier work from, as quantiles of *this* world's
 * own height and slope distributions rather than absolute magnitudes.
 *
 * Land only, and away from the edge, because that is the domain both callers care about: below sea
 * level is water by definition, and getSlopes stores a slope of 0 there, so including it would drag
 * every quantile toward nothing.
 */
function getThresholds(render) {
  const { h, slopeData } = render;
  const heights = [];
  const slopes = [];

  for (let i = 0; i < h.length; i++) {
    if (h[i] <= 0 || isnearedge(h.mesh, i)) continue;
    heights.push(h[i]);
    slopes.push(slopeData.get(i) || 0);
  }

  return {
    // The flattest half or so of the country is plain; the steepest tenth is where mountains can
    // begin.
    slopePlain: quantile(slopes, 0.45),
    slopeHill: quantile(slopes, 0.7),
    // The router divides by this, so it is the scale that makes COST.SLOPE mean "how much worse is
    // the steepest tenth than the flat".
    slopeRef: quantile(slopes, 0.9) || 1,
    heightHigh: quantile(heights, 0.5),
  };
}

function getMap(render) {
  time(render, "features", () => {
    render.rivers = getRivers(render.h, 0.007);
    render.coasts = contour(render.h, 0);
    render.terr = getTerritories(render);
    render.borders = getBorders(render);
    const { magnitude, components } = computeSlopeData(render.h);
    render.slopes = slopeStrokes(render.h, components);
    // Kept on the render object: the router consumes it, and scripts/mapstat.js needs the
    // distribution to check the terrain-classification quantiles.
    render.slopeData = magnitude;
    render.thresholds = getThresholds(render);
  });

  time(render, "roads", () => {
    render.roads = buildRoad(render, render.slopeData);
  });
  const [cities, regions] = time(render, "labels", () => getLabels(render));
  render.cityLabels = cities;
  render.regionLabels = regions;
  render.political = getPolitical(cities, regions, render.route);

  debug(render.features);
  debug(render.route);
  debug(render.political);
  return render;
}

const GameMap = (params = MapConfig()) => {
  debug("Getting Game Map", params.seed);

  // Everything stateful about randomness is reset here, so one call to GameMap is one whole world
  // and nothing leaks in from the last one.
  setSeed(params.seed);
  resetRnorm();
  resetNamePools();

  let render = { params };

  render.h = time(render, "coast", () => generateCoast(params, render));
  time(render, "cities", () => placeCities(render));
  getMap(render);

  return render;
};

export default GameMap;
